import type { User, Place, PlaceDetail, AddVisitFormData, SasTokenResponse, CountryVisit } from '../types';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error(err.error || 'Request failed'), { status: res.status });
  }
  return res.json() as Promise<T>;
}

export function getUser(): Promise<User> {
  return request<User>('/api/user');
}

export function listPlaces(): Promise<Place[]> {
  return request<Place[]>('/api/places');
}

export function getPlace(placeId: string): Promise<PlaceDetail> {
  return request<PlaceDetail>(`/api/places/${placeId}`);
}

export function createPlace(data: AddVisitFormData): Promise<{ placeId: string; userPlaceId: string }> {
  return request('/api/places', { method: 'POST', body: JSON.stringify(data) });
}

export function getBlobSasToken(ext = 'jpg', placeId?: string): Promise<SasTokenResponse> {
  const params = new URLSearchParams({ ext });
  if (placeId) params.set('placeId', placeId);
  return request<SasTokenResponse>(`/api/blob-sas?${params}`);
}

export function updatePlacePhotos(
  placeId: string,
  photos: { photoURL1: string | null; photoURL2: string | null; photoURL3: string | null; photoURL4: string | null; photoURL5: string | null }
): Promise<{ updated: boolean }> {
  return request(`/api/places/${placeId}/photos`, { method: 'PATCH', body: JSON.stringify(photos) });
}

export function listCountries(scope: 'mine' | 'family' | 'all' = 'mine'): Promise<CountryVisit[]> {
  return request<CountryVisit[]>(`/api/countries?scope=${scope}`);
}

export function deletePlace(placeId: string): Promise<{ deleted: boolean }> {
  return request(`/api/places/${placeId}`, { method: 'DELETE' });
}

export function updatePlace(
  placeId: string,
  data: { visitType: string; visitDate: string; notes: string; userImageBlobURL: string | null; soloTraveller: string | null; placeName?: string }
): Promise<{ updated: boolean }> {
  return request(`/api/places/${placeId}`, { method: 'PATCH', body: JSON.stringify(data) });
}
